import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Link,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatPhoneNumber } from '@fortress-technology-solutions/fortress-component-library/utils';
import type { Conversation } from '../types';

type Props = {
  conversation: ?Conversation,
};

function ThreadHeader({ conversation }: Props): React$Node {
  const currentOrigin = window.location.origin;
  const recipient = conversation?.recipient;

  const participantName =
    recipient?.customer?.fullName || recipient?.prospect?.fullName || 'Unknown';
  const lifecycleStatus =
    recipient?.customer?.lifecycleStatus ||
    recipient?.prospect?.lifecycleStatus ||
    'Unknown';
  const propertyName = recipient?.property?.name || 'Unknown Property';
  const phoneNumber = recipient?.phoneNumber || 'No phone';

  const residentId = recipient?.residentId;
  const applicationId = recipient?.applicationId;
  const prospectId = recipient?.prospectId;
  const propertyId = recipient?.property?.id;

  const profileUrl = useMemo(() => {
    if (!propertyId) return null;
    if (lifecycleStatus === 'Resident' && residentId) {
      return `${currentOrigin}/property/${propertyId}/resident/${residentId}`;
    } else if (lifecycleStatus === 'Applicant' && applicationId) {
      return `${currentOrigin}/property/${propertyId}/application/${applicationId}`;
    } else if (lifecycleStatus === 'Lead' && prospectId) {
      return `${currentOrigin}/property/${propertyId}/prospect/${prospectId}`;
    } else if (lifecycleStatus === 'Prior Resident' && residentId) {
      return `${currentOrigin}/property/${propertyId}/prior-resident/${residentId}`;
    }
    return null;
  }, [
    lifecycleStatus,
    residentId,
    applicationId,
    prospectId,
    currentOrigin,
    propertyId,
  ]);

  if (!conversation) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={0.5}>
        {profileUrl ? (
          <Link
            to={profileUrl}
            target="_blank"
            underline="hover"
            rel="noopener noreferrer"
            sx={{
              width: 'fit-content',
              cursor: 'pointer',
            }}
          >
            <Typography variant="titleMedium">{participantName}</Typography>
          </Link>
        ) : (
          <Typography variant="titleMedium">{participantName}</Typography>
        )}
        <Typography variant="bodySmall" color="text.secondary">
          {lifecycleStatus} • {propertyName}
        </Typography>
        <Typography variant="bodySmall" color="text.secondary">
          {formatPhoneNumber(phoneNumber.slice(-10))}
        </Typography>
      </Stack>
    </Box>
  );
}

export default ThreadHeader;
